import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as authApi from '../api/authApi';
import { isDemoAuth, isJwtAuth, isJwtOnly } from './authMode';
import { beginEntraSignIn, completeEntraRedirect, logoutEntra } from './entraClient';
import {
  clearAuthSessionMeta,
  clearAccessToken,
  getAccessToken,
  persistTokenPair,
  readAuthSessionMeta,
  toWorkspaceSession,
} from './authSession';
import {
  clearDemoSession,
  createDemoSession,
  readDemoSession,
  writeDemoSession,
} from './demoSession';
import { rememberPendingRole, takePendingRole } from './signInRole';
import {
  SESSION_EXPIRED_EVENT,
  SESSION_REFRESHED_EVENT,
  msUntilRefreshDue,
  refreshAccessToken as refreshSharedAccessToken,
} from './tokenRefresh';
import { getWorkspace, workspaceHomePath } from './workspaces';

const AuthContext = createContext(null);

function persistJwtSession(tokenPair, extras = {}) {
  const meta = persistTokenPair(tokenPair, { authProvider: extras.authProvider });
  // Clear demo session so dual mode does not mix identity sources.
  clearDemoSession();
  return toWorkspaceSession(meta);
}

function initialSession() {
  if (isJwtAuth()) {
    const meta = readAuthSessionMeta();
    if (meta) {
      return toWorkspaceSession(meta);
    }
  }
  if (isDemoAuth()) {
    return readDemoSession();
  }
  return null;
}

export function AuthProvider({ children }) {
  const jwtEnabled = isJwtAuth();
  const demoEnabled = isDemoAuth();
  const jwtOnly = isJwtOnly();
  const loginTokenRef = useRef(null);
  const membershipsRef = useRef([]);
  const pendingAuthProviderRef = useRef('password');
  const [ready, setReady] = useState(!(jwtEnabled && Boolean(readAuthSessionMeta()?.refreshToken)));

  const [session, setSession] = useState(() => initialSession());
  const [memberships, setMemberships] = useState([]);
  const [pendingLogin, setPendingLogin] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Single-flight refresh shared with authFetch (the backend rotates refresh
  // tokens, so two parallel refreshes would log the user out).
  const refreshAccessToken = useCallback(async () => {
    const meta = await refreshSharedAccessToken();
    clearDemoSession();
    const next = toWorkspaceSession(meta);
    setSession(next);
    return next;
  }, []);

  // Page load: the access token lives only in memory, so rebuild it from the
  // stored refresh token before rendering protected routes.
  useEffect(() => {
    if (!jwtEnabled) {
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    const meta = readAuthSessionMeta();
    if (!meta?.refreshToken) {
      setReady(true);
      return undefined;
    }
    refreshSharedAccessToken()
      .then((refreshed) => {
        if (cancelled) return;
        clearDemoSession();
        setSession(toWorkspaceSession(refreshed));
      })
      .catch(() => {
        if (cancelled) return;
        clearAuthSessionMeta();
        clearAccessToken();
        // Fall back to demo session in dual mode
        const demo = demoEnabled ? readDemoSession() : null;
        setSession(demo);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [jwtEnabled, demoEnabled]);

  // Keep React state in step with refreshes triggered from the API layer, and
  // drop the session when a refresh is refused (revoked / expired).
  useEffect(() => {
    if (!jwtEnabled || typeof window === 'undefined') return undefined;
    const onRefreshed = (event) => {
      setSession(toWorkspaceSession(event.detail || readAuthSessionMeta()));
    };
    const onExpired = () => {
      clearAccessToken();
      setSession(demoEnabled ? readDemoSession() : null);
      setAuthError('Your session has expired. Please sign in again.');
    };
    window.addEventListener(SESSION_REFRESHED_EVENT, onRefreshed);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(SESSION_REFRESHED_EVENT, onRefreshed);
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, [jwtEnabled, demoEnabled]);

  // Proactive renewal: refresh shortly before `exp` so a long-lived tab never
  // sends an expired access token.
  useEffect(() => {
    if (!jwtEnabled || !session?.refreshToken || !getAccessToken()) return undefined;
    const due = msUntilRefreshDue();
    if (due === null) return undefined;
    const timer = setTimeout(
      () => {
        refreshSharedAccessToken().catch(() => {
          // SESSION_EXPIRED_EVENT listener clears state.
        });
      },
      Math.max(due, 1000)
    );
    return () => clearTimeout(timer);
  }, [jwtEnabled, session]);

  const selectWorkspace = useCallback(
    (workspaceId, tenantId) => {
      if (!demoEnabled || jwtOnly) {
        return null;
      }
      // Switching to demo clears any JWT session
      clearAuthSessionMeta();
      clearAccessToken();
      const next = createDemoSession(workspaceId, tenantId);
      if (!next) {
        return null;
      }
      writeDemoSession(next);
      setSession(next);
      setPendingLogin(null);
      return next;
    },
    [demoEnabled, jwtOnly]
  );

  const applyLoginResult = useCallback(
    (result, authProvider = 'password', workspaceType = null) => {
      loginTokenRef.current = result.login_token;
      membershipsRef.current = result.memberships || [];
      pendingAuthProviderRef.current = authProvider;
      setMemberships(result.memberships || []);
      setPendingLogin({
        userId: result.user_id,
        email: result.email,
        fullName: result.full_name,
        isPlatformAdmin: result.is_platform_admin,
        // the persona chosen on the sign-in page (memberships are already
        // limited to it by the backend)
        workspaceType: workspaceType || null,
      });
      return result;
    },
    []
  );

  const signIn = useCallback(
    async (email, password, workspaceType) => {
      setAuthError(null);
      const result = await authApi.login(email, password, workspaceType);
      return applyLoginResult(result, 'password', workspaceType);
    },
    [applyLoginResult]
  );

  const signInWithMicrosoft = useCallback(async (workspaceType) => {
    setAuthError(null);
    // The redirect leaves the SPA; the callback page picks the role back up.
    rememberPendingRole(workspaceType);
    await beginEntraSignIn();
  }, []);

  const completeMicrosoftSignIn = useCallback(async () => {
    setAuthError(null);
    const workspaceType = takePendingRole();
    const redirected = await completeEntraRedirect();
    if (!redirected?.accessToken) {
      throw new Error('Microsoft sign-in did not return an access token.');
    }
    const result = await authApi.entraLogin(redirected.accessToken, workspaceType);
    return applyLoginResult(result, 'entra', workspaceType);
  }, [applyLoginResult]);

  const selectOrganization = useCallback(async (organizationId) => {
    setAuthError(null);
    const tokenPair = await authApi.createSession({
      organizationId,
      loginToken: loginTokenRef.current,
      accessToken: getAccessToken(),
    });
    const next = persistJwtSession(tokenPair, {
      authProvider: pendingAuthProviderRef.current || 'password',
    });
    setSession(next);
    setPendingLogin(null);
    loginTokenRef.current = null;
    return next;
  }, []);

  const applyTokenPair = useCallback((tokenPair) => {
    const next = persistJwtSession(tokenPair, {
      authProvider: pendingAuthProviderRef.current || 'password',
    });
    setSession(next);
    setPendingLogin(null);
    return next;
  }, []);

  const signOut = useCallback(async () => {
    const meta = readAuthSessionMeta();
    if (meta?.refreshToken) {
      try {
        await authApi.logout(meta.refreshToken);
      } catch {
        // ignore logout errors
      }
    }
    const entraSession = meta?.authProvider === 'entra';
    clearAuthSessionMeta();
    clearAccessToken();
    clearDemoSession();
    setSession(null);
    setPendingLogin(null);
    setMemberships([]);
    loginTokenRef.current = null;
    pendingAuthProviderRef.current = 'password';
    if (entraSession) {
      try {
        await logoutEntra();
      } catch {
        // local sign-out still completed
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      jwtMode: jwtEnabled,
      demoMode: demoEnabled,
      ready,
      session,
      workspace: session ? getWorkspace(session.workspaceId) : null,
      selectWorkspace,
      signOut,
      homePath: session ? workspaceHomePath(session.workspaceId) : '/signin',
      signIn,
      signInWithMicrosoft,
      completeMicrosoftSignIn,
      applyLoginResult,
      selectOrganization,
      applyTokenPair,
      refreshAccessToken,
      memberships,
      pendingLogin,
      authError,
      setAuthError,
      getAccessToken,
    }),
    [
      jwtEnabled,
      demoEnabled,
      ready,
      session,
      selectWorkspace,
      signOut,
      signIn,
      signInWithMicrosoft,
      completeMicrosoftSignIn,
      applyLoginResult,
      selectOrganization,
      applyTokenPair,
      refreshAccessToken,
      memberships,
      pendingLogin,
      authError,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return value;
}

/** Alias so existing call sites keep working. */
export function useDemoSession() {
  return useAuth();
}
